import sileo from 'sileo';
// Board Related Models
const Board = new sileo.Model('board', 'boards');
const BoardDetails = new sileo.Model('board', 'board-details');
const MainBoardMembers = new sileo.Model('board', 'main-board-members');
const CustomColumns = new sileo.Model('board', 'custom-columns');
const BoardSpace = new sileo.Model('board', 'board-space');
const LeaveBoardSpace = new sileo.Model('board', 'leave-board-space');
const BoardSpaceComment = new sileo.Model('board', 'board-space-comment');
const BoardSpaceMilestoneChats = new sileo.Model('board', 'board-space-milestone-chats');
const BoardSpaceMentionChats = new sileo.Model('board', 'board-space-mention-chats');
const BoardSpaceMembers = new sileo.Model('board', 'board-space-members');
const Columns = new sileo.Model('board', 'board-columns');
const AITagCategorization = new sileo.Model('board', 'ai-tag-categorization');

// Card Related Models
const MainBoardCardPanel = new sileo.Model('board', 'card-panel');
const MainBoardCardDetails = new sileo.Model('board', 'main-board-card-details');
const MainBoardDuplicateCard = new sileo.Model('board', 'duplicate-card');
const MainBoardCardDetailsActivities = new sileo.Model('board', 'main-board-card-details-activities');
const MainBoardCardMembers = new sileo.Model('board', 'main-board-card-members');
const MainBoardCardSubtasks = new sileo.Model('board', 'main-board-card-subtasks');
const MainBoardCardTaskAssignments = new sileo.Model('board', 'main-board-card-task-assignments');
const MainBoardCardForwardedBoard = new sileo.Model('board', 'main-board-card-forwarded-board');
const MainBoardCardEstimation = new sileo.Model('board', 'main-board-card-estimation');
const MilestoneCardRequests = new sileo.Model('hqzen', 'milestone-card-requests');
const ActivityLog = new sileo.Model('board', 'activity-log');
const Screenshot = new sileo.Model('board', 'assignment-screenshots');
const TaskAssignment = new sileo.Model('board', 'task-assignment');
const JoinCard = new sileo.Model('board', 'join-card');
const CardMemberUsers = new sileo.Model('board', 'card-member-users');
const TransferCard = new sileo.Model('board', 'transfer-card');
const RemoveMultipleCardSubtasks = new sileo.Model('board', 'remove-multiple-card-subtask');

// Command Board Card Models
const ForwardedCard = new sileo.Model('board', 'forwarded-card');

// Estimation Related Models
const CardEstimation = new sileo.Model('board', 'card-estimation');
const CardEstimationResponse = new sileo.Model('board', 'card-estimation-response');
const CardEstimators = new sileo.Model('board', 'card-estimators');
const EstimationQuestionSets = new sileo.Model('board', 'estimation-question-sets');

// for request to join a card
const CardRequest = new sileo.Model('board', 'card-requests');

// for multiple card request acceptances and rejections
const MultipleCardRequest = new sileo.Model('board', 'update-multiple-card-request');

/**
 * Gets the hideClosedColumnMap data from local storage.
 * @returns {object} The hideClosedColumnMap or an empty object.
 */
const _getClosedColumnMap = () => {
    const mapJSONString = localStorage.getItem('closed-column-map');

    if (!mapJSONString) return {};

    try {
        const showClosedColumnMap = JSON.parse(mapJSONString);
        return showClosedColumnMap;
    } catch {
        console.error('Error in _getShowClosedColumnMap');
        return {};
    }
};

const state = {
    selectedColumn: {},
    previousCard: null,

    // These properties govern the Board Filter
    // component's filter fields.
    selectedAuthors: [],
    selectedExcludeAuthors: [],
    selectedAssignees: [],
    selectedExcludeAssignees: [],
    selectedIssuers: [],
    selectedExcludeIssuers: [],
    selectedMembers: [],
    selectedExcludeMembers: [],
    selectedMilestones: [],
    selectedExcludeMilestones: [],
    selectedTags: [],
    selectedExcludeTags: [],
    selectedSorter: 'newest',
    selectedStartDate: '',
    selectedEndDate: '',
    selectedCreationStartDate: '',
    selectedCreationEndDate: '',

    selectedQuickFilter: '', // string or object.
    selectedQuickSubFilters: [],
    selectedDueDatePresets: [
        { name: 'Overdue', selected: false },
        { name: 'Due Today', selected: false },
        { name: 'Due Tomorrow', selected: false },
    ].map(preset => ({ ...preset })),
    /**
     * This is an object with user IDs as keys and a list of board IDs as value.
     * If a board ID is here, that means the "Closed" column will not be visible.
     */

    hideClosedColumnMap: _getClosedColumnMap()
};

const getters = {
    hideClosedColumn: (state) => (userId) => state.hideClosedColumnMap[userId],
};

const mutations = {
    /*
      preserve the id of the previously open card activity to enable smooth return
    */
    setPreviousCard(state, data) {
        state.previousCard = data;
    },

    setSelectedAuthors(state, data) {
        state.selectedAuthors = data;
    },

    setSelectedExcludeAuthors(state, data) {
        state.selectedExcludeAuthors = data;
    },

    setSelectedAssignees(state, data) {
        state.selectedAssignees = data;
    },

    setSelectedExcludeAssignees(state, data) {
        state.selectedExcludeAssignees = data;
    },

    setSelectedIssuers(state, data) {
        state.selectedIssuers = data;
    },

    setSelectedExcludeIssuers(state, data) {
        state.selectedExcludeIssuers = data;
    },

    setSelectedMembers(state, data) {
        state.selectedMembers = data;
    },

    setSelectedExcludeMembers(state, data) {
        state.selectedExcludeMembers = data;
    },

    setSelectedMilestones(state, data) {
        state.selectedMilestones = data;
    },

    setSelectedExcludeMilestones(state, data) {
        state.selectedExcludeMilestones = data;
    },

    setSelectedTags(state, data) {
        state.selectedTags = data;
    },

    setSelectedExcludeTags(state, data) {
        state.selectedExcludeTags = data;
    },

    setSelectedSorter(state, data) {
        state.selectedSorter = data;
    },

    setSelectedStartDate(state, data) {
        state.selectedStartDate = data;
    },

    setSelectedEndDate(state, data) {
        state.selectedEndDate = data;
    },

    setSelectedCreationStartDate(state, data) {
        state.selectedCreationStartDate = data;
    },

    setSelectedCreationEndDate(state, data) {
        state.selectedCreationEndDate = data;
    },

    toggleSelectedPreset(state, { name, selected }) {
        const toUpdatePreset = state.selectedDueDatePresets.find(view => view.name === name);
        if (toUpdatePreset) {
            toUpdatePreset.selected = selected;
        }
    },

    resetSelectedPresets(state) {
        state.selectedDueDatePresets = [
            { name: 'Overdue', selected: false },
            { name: 'Due Today', selected: false },
            { name: 'Due Tomorrow', selected: false },
        ].map(preset => ({ ...preset }));
    },

    setSelectedQuickFilter(state, data) {
        state.selectedQuickFilter = data;
    },

    setselectedQuickSubFilters(state, data) {
        state.selectedQuickSubFilters = data;
    },

    updateSelectedQuickSubFilters(state, { data, op = 'add' }) {
        if (op === 'add' && !(
            state.selectedQuickSubFilters.includes(data)
        ))
            state.selectedQuickSubFilters.push(data);
        else if (op === 'remove')
            state.selectedQuickSubFilters = state.selectedQuickSubFilters.filter(
                filter => filter !== data
            );
    },

    /**
     * Add a board ID of the given user from the hideClosedColumnMap
     */
    addToHideClosedColumnMap(state, { userId, boardId }) {
        if (!boardId || !userId) return;

        const hideClosedColumnMap = { ...state.hideClosedColumnMap };
        const existingBoardIds = hideClosedColumnMap[userId] ? [...hideClosedColumnMap[userId]] : [];
        hideClosedColumnMap[userId] = [...existingBoardIds, boardId];

        // Update this in local storage
        const mapJSONString = JSON.stringify(hideClosedColumnMap);
        localStorage.setItem('closed-column-map', mapJSONString);

        state.showClosedColumnMap = hideClosedColumnMap;
    },

    /**
     * Remove a board ID of the given user from the hideClosedColumnMap
     */
    removeFromHideClosedColumnMap(state, { userId, boardId }) {
        if (!boardId || !userId) return;

        const hideClosedColumnMap = { ...state.hideClosedColumnMap };
        const boardIds = hideClosedColumnMap[userId] ? [...hideClosedColumnMap[userId]] : [];

        const index = boardIds.indexOf(boardId);
        if (index === -1) return;

        boardIds.splice(index, 1);
        hideClosedColumnMap[userId] = boardIds;

        // Update this in local storage
        const mapJSONString = JSON.stringify(hideClosedColumnMap);
        localStorage.setItem('closed-column-map', mapJSONString);

        state.showClosedColumnMap = hideClosedColumnMap;
    },
};

const actions = {
    /*******************************************
     *    ---- Board Related Actions ----
     ******************************************/
    deleteBoard(context, filters) {
        return Board.objects.delete(filters);
    },

    createBoard(context, data) {
        const form = new FormData();

        for (let field in data) {
            form.append(field, data[field]);
        }

        return Board.objects.create(form);
    },

    fetchCurrentBoardMembers(context, filters) {
        return MainBoardMembers.objects.filter(filters);
    },

    updateBoard(context, data) {
        const form = new FormData();
        const pk = data.pk,
            dataKeys = Object.keys(data);

        const fieldsToConvertToJSON = ['estimation_question_sets', 'updated_custom_columns', 'deleted_custom_columns'];
        const fieldsToJoinArray = ['custom_todo', 'custom_doing'];

        dataKeys.forEach(key => {
            if (fieldsToConvertToJSON.includes(key) && data[key]?.length > 0) {
                data[key] = JSON.stringify(data[key]);
            } else if (fieldsToJoinArray.includes(key) && data[key]?.length > 0) {
                data[key] = data[key].join();
            }
            form.append(key, data[key]);
        });

        return Board.objects.update({ 'pk': pk }, form);
    },

    updateBoardDetails(context, data) {
        const form = new FormData();
        let pk = data.pk,
            dataKeys = Object.keys(data);

        dataKeys.forEach(key => {
            form.append(key, data[key]);
        });

        return BoardDetails.objects.update({ 'pk': pk }, form);
    },

    createCustomColumn(context, form) {
        return CustomColumns.objects.create(form);
    },

    updateCustomColumn(context, data) {
        const form = new FormData();
        let pk = data.pk,
            dataKeys = Object.keys(data);

        dataKeys.forEach(key => {
            form.append(key, data[key]);
        });
        return CustomColumns.objects.update({ 'pk': pk }, form);
    },

    updateCustomColumnPositions(context, data) {
        let { pk, columns } = data;
        const form = new FormData();
        form.append('positions', JSON.stringify(columns));
        return CustomColumns.objects.update({ 'board_id': pk }, form);
    },

    deleteCustom(context, filters) {
        return CustomColumns.objects.delete(filters);
    },

    /*******************************************
     *    ---- Board Space Actions ----
     ******************************************/
    createBoardSpace(context, data) {
        const form = new FormData();

        for (let field in data) {
            form.append(field, data[field]);
        }

        return BoardSpace.objects.create(form);
    },

    updateBoardSpace(context, data) {
        const form = new FormData();
        const object_id = data['pk'];
        delete data['pk'];
        for (const field in data) {
            form.append(field, data[field]);
        }

        return BoardSpace.objects.update({ 'pk': object_id }, form);
    },

    deleteBoardSpace(context, filters) {
        return BoardSpace.objects.delete(filters);
    },

    fetchBoardSpaces(context, filters) {
        return BoardSpace.objects.filter(filters);
    },

    fetchBoardSpaceMembers(context, filters) {
        return BoardSpaceMembers.objects.filter(filters);
    },

    fetchBoardSpaceComments(context, filters) {
        return BoardSpaceComment.objects.filter(filters);
    },

    createBoardSpaceComment(context, data) {
        const form = new FormData();

        for (let field in data) {
            form.append(field, data[field]);
        }

        return BoardSpaceComment.objects.create(form);
    },

    deleteBoardSpaceComment(context, filters) {
        return BoardSpaceComment.objects.delete(filters);
    },

    updateBoardSpaceComment(context, data) {
        const form = new FormData();
        const object_id = data['pk'];
        delete data['pk'];
        for (const field in data) {
            form.append(field, data[field]);
        }

        return BoardSpaceComment.objects.update({ 'pk': object_id }, form);
    },

    fetchBoardSpaceMilestoneChats(context, filters) {
        return BoardSpaceMilestoneChats.objects.filter(filters);
    },

    fetchBoardSpaceMentionChats(context, filters) {
        return BoardSpaceMentionChats.objects.filter(filters);
    },

    leaveBoardSpace(context, data) {
        const form = new FormData();
        const pk = data['pk'];
        delete data['pk'];
        for (const field in data) {
            form.append(field, data[field]);
        }

        return LeaveBoardSpace.objects.update({ 'pk': pk }, form);
    },

    fetchColumns(context, { filters, excludes = {} }) {
        return Columns.objects.filter(filters, excludes);
    },

    /************************************************
     *       ---- Card Related Actions ----
     ************************************************/

    setPreviouslySelectedCard({ commit }, details) {
        commit('setPreviousCard', details);
    },

    getCardDetails(context, pk) {
        return MainBoardCardDetails.objects.get(pk);
    },

    getCardMembers(context, pk) {
        return MainBoardCardMembers.objects.get(pk);
    },

    fetchCardDetailsActivities(context, filters) {
        return MainBoardCardDetailsActivities.objects.filter(filters);
    },

    /** TODO: (jvcaballero@bposeats.com) retaining the
     * original parameters here so as not to break the
     * current functionalities, as the goal for now is
     * to reorganize the store structure. This needs
     * to be refactored in the polishing phase
    */
    editCardDetails(context, { data, routeName }) {
        let form = new FormData();
        let filters = { pk: data.pk };

        for (let key of Object.keys(data)) {
            form.append(key, data[key]);
        }

        if (routeName === 'board-main' || routeName === 'milestone-details')
            return MainBoardCardDetails.objects.update(filters, form);
        return;
    },

    fetchAssignmentScreenshots(context, filters) {
        return Screenshot.objects.filter(filters);
    },

    getTaskAssignment(context, pk) {
        return TaskAssignment.objects.get(pk);
    },

    fetchActivityLogs(context, filters) {
        return ActivityLog.objects.filter(filters);
    },

    fetchCardRequests(context, filters) {
        return CardRequest.objects.filter(filters);
    },

    fetchUserPendingCardRequest(context, filters) {
        return CardRequest.objects.filter(filters);
    },

    requestToJoinPrivateCard(context, data) {
        const form = new FormData();

        for (let field in data) {
            form.append(field, data[field]);
        }

        return CardRequest.objects.create(form);
    },

    updateCardRequest(context, data) {
        const form = new FormData();

        let pk = data.pk,
            dataKeys = Object.keys(data);

        dataKeys.forEach(key => {
            form.append(key, data[key]);
        });

        return CardRequest.objects.update({ 'pk': pk }, form);
    },

    updateMultipleCardRequests(context, data) {
        const form = new FormData();
        let pk = data.pk;

        for (let field in data) {
            if (field === 'requests') {
                data['requests'] = JSON.stringify(data.requests);
            }
            form.append(field, data[field]);
        }

        return MultipleCardRequest.objects.update({ 'pk': pk }, form);
    },

    updateCard(context, data) {
        let form = new FormData();

        for (let key of Object.keys(data)) {
            form.append(key, data[key]);
        }

        return MainBoardCardPanel.objects.update({ 'pk': data.pk }, form);
    },

    fetchCardMembers(context, pk) {
        return CardMemberUsers.objects.get(pk);
    },

    joinCard(context, data) {
        const form = new FormData();

        for (let field in data) {
            form.append(field, data[field]);
        }

        return JoinCard.objects.create(form);
    },

    updateUserCardRequest(context, data) {
        return JoinCard.objects.update(data);
    },

    fetchCardRequest(context, pk) {
        return JoinCard.objects.get(pk);
    },

    createCard(context, data) {
        const form = new FormData();

        for (let field in data) {
            form.append(field, data[field]);
        }

        return MainBoardCardPanel.objects.create(form);
    },

    async duplicateCard(context, data) {
        let form = new FormData();

        for (let field in data)
            form.append(field, data[field]);

        return await MainBoardDuplicateCard.objects.create(form);
    },

    async getCardDuplicationData(context, pk) {
        return MainBoardDuplicateCard.objects.get(pk);
    },

    getCardForwardedBoard(context, pk) {
        return MainBoardCardForwardedBoard.objects.get(pk);
    },

    fetchTaskAssignments(context, filters) {
        return MainBoardCardTaskAssignments.objects.filter(filters);
    },

    /********************************************************
     *        ---- Estimation Related Actions ----
     *******************************************************/

    getCardEstimation(context, pk) {
        return MainBoardCardEstimation.objects.get(pk);
    },

    createCardEstimation(context, data) {
        const form = new FormData();

        for (let field in data) {
            form.append(field, data[field]);
        }

        return CardEstimation.objects.create(form);
    },

    updateCardEstimation(context, data) {
        const form = new FormData();
        let pk = data.pk;

        for (let field in data) {
            form.append(field, data[field]);
        }

        return CardEstimation.objects.update({ 'pk': pk }, form);
    },

    fetchCardEstimationResponse(context, pk) {
        return CardEstimationResponse.objects.get(pk);
    },

    fetchEstimationQuestionSets(context, filters) {
        return EstimationQuestionSets.objects.filter(filters);
    },

    createEstimationQuestionSet(context, data) {
        const form = new FormData();

        for (let field in data) {
            form.append(field, data[field]);
        }

        return EstimationQuestionSets.objects.create(form);
    },

    updateEstimationQuestionSet(context, data) {
        const form = new FormData();
        let pk = data.pk;

        for (let field in data) {
            form.append(field, data[field]);
        }
        return EstimationQuestionSets.objects.update({ 'pk': pk }, form);
    },

    createCardEstimationResponse(context, data) {
        const form = new FormData();

        for (let field in data) {
            form.append(field, data[field]);
        }
        return CardEstimationResponse.objects.create(form);
    },

    updateCardEstimators(context, { filters, estimators }) {
        let form = new FormData();
        form.append('estimators', estimators);
        return CardEstimators.objects.update(filters, form);
    },

    updateCardEstimationResponse(context, data) {
        const form = new FormData();
        let pk = data.pk;

        for (let field in data) {
            form.append(field, data[field]);
        }
        return CardEstimationResponse.objects.update({ 'pk': pk }, form);
    },

    /********************************************************
    *        ---- Subtask Related Actions ----
    *******************************************************/

    fetchCardSubtasks(context, filters) {
        return MainBoardCardSubtasks.objects.filter(filters);
    },

    createCardSubtask(context, data) {
        const form = new FormData();

        for (let field in data) {
            form.append(field, data[field]);
        }

        return MainBoardCardSubtasks.objects.create(form);
    },

    deleteCardSubtask(context, filters) {
        return MainBoardCardSubtasks.objects.delete(filters);
    },

    deleteMultipleCardSubtask(context, filters) {
        return RemoveMultipleCardSubtasks.objects.delete(filters);
    },

    updateCardSubtask(context, data) {
        const form = new FormData();
        let pk = data.pk;

        for (let field in data) {
            form.append(field, data[field]);
        }

        return MainBoardCardSubtasks.objects.update({ 'pk': pk }, form);
    },

    /********************************************************
   *    ---- Milestone Card Request Related Actions ----
   *******************************************************/

    fetchMilestoneCardRequest(context, filters) {
        return MilestoneCardRequests.objects.filter(filters);
    },

    createMilestoneCardRequest(context, data) {
        const form = new FormData();

        for (let key of Object.keys(data)) {
            form.append(key, data[key]);
        }

        return MilestoneCardRequests.objects.create(form);
    },

    updateMilestoneCardRequest(context, data) {
        const form = new FormData();
        let pk = data.pk,
            dataKeys = Object.keys(data);

        dataKeys.forEach(key => {
            form.append(key, data[key]);
        });
        return MilestoneCardRequests.objects.update({ 'pk': pk }, form);
    },

    async transferCard(context, data) {
        const form = new FormData();
        let pk = data.pk,
            dataKeys = Object.keys(data);

        dataKeys.forEach(key => {
            form.append(key, data[key]);
        });

        return await TransferCard.objects.update({ 'pk': pk }, form);
    },

    updateAICategorizationSetting(context, filters) {
        return AITagCategorization.objects.update(filters);
    },

    /********************************************************
     *        ---- Command Board Related Actions ----
     *******************************************************/

    createForwardedCard(context, data) {
        const form = new FormData();
        for (let key of Object.keys(data)) {
            form.append(key, data[key]);
        }

        return ForwardedCard.objects.create(form);
    }
};

export default {
    namespaced: true,
    state,
    getters,
    mutations,
    actions,
};
